import { NativeEventEmitter } from 'react-native';
import { getMacAudioCapture, requestRecordingPermissionsAsync } from './macAudioRecording';
import { MAC_VOICE_SCRIPT } from './macVoicePage';

let activePeer = null;
let nextPeer = 0;

// Audio-only WebRTC surface consumed by VoiceSocketManager. WebKit owns the
// actual tracks and remote playback; RN owns session/API state.
class MacVoicePeerConnection {
  constructor() {
    if (activePeer) throw new Error('A voice connection is already active. End it before starting another.');
    this.native = getMacAudioCapture();
    activePeer = this;
    this.id = `voice-${Date.now()}-${++nextPeer}`;
    this.closed = false;
    this.connectionState = 'new';
    this.level = 0;
    this.channel = null;
    this.subscription = new NativeEventEmitter(this.native).addListener('onMacVoiceEvent', event => {
      if (event.id !== this.id || this.closed) return;
      if (event.type === 'connection') {
        this.connectionState = event.state;
        this.onconnectionstatechange?.();
      } else if (event.type === 'level') {
        this.level = event.level;
      } else if (event.type === 'error') {
        this.channel?.onerror?.(new Error(event.message));
        this.connectionState = 'failed';
        this.onconnectionstatechange?.();
      } else if (this.channel) {
        if (event.type === 'open') this.channel.readyState = 'open';
        if (event.type === 'close') this.channel.readyState = 'closed';
        this.channel[`on${event.type}`]?.({ data: event.data });
      }
    });
    this.ready = (async () => {
      const permission = await requestRecordingPermissionsAsync();
      if (this.closed) throw new Error('Voice connection closed');
      if (!permission.granted) throw new Error('MICROPHONE_PERMANENTLY_DENIED: Allow Gab AI microphone access in System Settings.');
      await this.native.prepareVoice(this.id, MAC_VOICE_SCRIPT);
      if (this.closed) throw new Error('Voice connection closed');
    })();
    this.ready.catch(() => {});
  }
  async command(command, payload = {}) {
    await this.ready;
    if (this.closed) throw new Error('Voice connection closed');
    return this.native.voiceCommand(this.id, command, payload);
  }
  sendCommand(command, payload) {
    this.command(command, payload).catch(error => { if (!this.closed) this.channel?.onerror?.(error); });
  }
  async microphone() {
    await this.command('microphone');
    const peer = this;
    let enabled = true;
    const track = {
      readyState: 'live',
      get enabled() { return enabled; },
      set enabled(value) { enabled = value; peer.sendCommand('mute', { muted: !value }); },
      stop() {
        if (track.readyState === 'ended') return;
        track.readyState = 'ended';
        if (!peer.closed) peer.sendCommand('stopInput');
      },
    };
    this.stream = { get active() { return track.readyState === 'live'; }, getTracks: () => [track], getAudioTracks: () => [track] };
    return this.stream;
  }
  addTrack() {} // WebKit attaches the acquired microphone track.
  createDataChannel() {
    this.channel = {
      readyState: 'connecting',
      send: data => this.sendCommand('send', { data }),
      close: () => { this.channel.readyState = 'closed'; },
    };
    return this.channel;
  }
  createOffer() { return this.command('offer'); }
  setLocalDescription(offer) { return this.command('local', offer); }
  setRemoteDescription(answer) { return this.command('answer', answer); }
  getSenders() { return this.stream?.getTracks().map(track => ({ track })) || []; }
  close() {
    if (this.closed) return;
    this.closed = true;
    this.stream?.getTracks().forEach(track => track.stop());
    this.subscription.remove();
    this.native.closeVoice(this.id);
    this.connectionState = 'closed';
    if (activePeer === this) activePeer = null;
  }
}

export function getRealtimeWebRTCAdapter() {
  return {
    RTCPeerConnection: MacVoicePeerConnection,
    RTCSessionDescription: null,
    mediaDevices: { getUserMedia: () => {
      if (!activePeer) return Promise.reject(new Error('Start a voice connection before acquiring the microphone.'));
      return activePeer.microphone();
    } },
    isNative: true,
  };
}

export const getMacVoiceInputVolume = () => activePeer?.level || 0;
export async function configureNativeRealtimeAudio() {}
export async function restoreNativeRealtimeAudio() {}
